/**
 * `#uc2Chips` — "View the evidence on the map" (client §2, design §3).
 *
 * Six exclusive chips: Overall change status (the three-class map, the default),
 * Land-cover change, Cropland change, Rainfall, Practices and Greening — the last greyed
 * until the vegetation archive completes, then enabled by the run itself
 * (`summary.greening.gated === false`), with no redeploy (client §6.4).
 *
 * Exclusive on purpose: one layer at a time is what keeps the map readable, and flipping
 * chips to see the same watershed through different evidence *is* the analysis experience
 * (client §2's own rule).
 *
 * `#uc2Legend` under the chips names the classes the active chip actually painted, with
 * the count of watersheds in each (NOTES #729) — so nothing on screen is unexplained.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { ChipHelp, type ChipHelpClass } from './ChipHelp';
import type { Uc2Class } from './uc2Paint';
import {
  CROPLAND_BASE,
  familySteps,
  normaliseChip,
  PRACTICE_BASE,
  RAINFALL_BASE,
  UC2_CATEGORY_COLORS,
  UC2_CHIPS,
  UC2_GREENING_FALLBACK_CLASSES,
  UC2_LC_CHANGE_COLORS,
  UC2_WATERSHED_ONLY_CHIPS,
  type Uc2Chip,
} from './uc2Constants';

/** A class label as a `data-testid` suffix — lower case, non-letters collapsed to `-`. */
function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * The swatch rows of a chip's hover card — each chip's own palette, read from
 * `uc2Constants` rather than respelled, so the card and the map can never disagree about
 * what a colour means. The three-step families are listed darkest-meaning first, the way
 * the sentences read.
 */
function helpClasses(id: Uc2Chip, t: (key: string) => string): ChipHelpClass[] {
  const label = (cls: string) => t(`intel.uc2.help.${id}.${cls}`);
  const cropland = familySteps(CROPLAND_BASE);
  const rainfall = familySteps(RAINFALL_BASE);
  const practice = familySteps(PRACTICE_BASE);
  switch (id) {
    case 'pattern':
      return [
        { id: 'positive', color: UC2_CATEGORY_COLORS.positive!, label: label('positive') },
        { id: 'no_pattern', color: UC2_CATEGORY_COLORS.no_pattern!, label: label('no_pattern') },
        { id: 'not_assessed', color: null, label: label('not_assessed') },
      ];
    case 'landcover':
      return [
        { id: 'gain', color: UC2_LC_CHANGE_COLORS.crop_gain, label: label('gain') },
        { id: 'loss', color: UC2_LC_CHANGE_COLORS.crop_loss, label: label('loss') },
        { id: 'veg_gain', color: UC2_LC_CHANGE_COLORS.veg_gain, label: label('veg_gain') },
        { id: 'veg_loss', color: UC2_LC_CHANGE_COLORS.veg_loss, label: label('veg_loss') },
        { id: 'none', color: UC2_LC_CHANGE_COLORS.none, label: label('none') },
      ];
    case 'cropland':
      return [
        { id: 'growing', color: cropland[2], label: label('growing') },
        { id: 'stable', color: cropland[1], label: label('stable') },
        { id: 'declining', color: cropland[0], label: label('declining') },
      ];
    case 'rainfall':
      return [
        { id: 'wetter', color: rainfall[2], label: label('wetter') },
        { id: 'stable', color: rainfall[1], label: label('stable') },
        { id: 'drier', color: rainfall[0], label: label('drier') },
      ];
    case 'practices':
      return [
        { id: 'low', color: practice[0], label: label('low') },
        { id: 'mid', color: practice[1], label: label('mid') },
        { id: 'high', color: practice[2], label: label('high') },
      ];
    case 'greening':
      return [
        { id: 'greening', color: UC2_GREENING_FALLBACK_CLASSES[0]!.color, label: label('greening') },
        { id: 'browning', color: UC2_GREENING_FALLBACK_CLASSES[1]!.color, label: label('browning') },
      ];
  }
}

export interface Uc2ChipsProps {
  /** The active chip's legend rows, straight off `uc2Paint`. */
  classes: Uc2Class[];
  /** Printed instead of the classes when the chip has none (the unpicked `similar` chip). */
  note: string | null;
  /** `summary.greening.gated === false` — the archive is complete and the chip is live. */
  greeningOpen: boolean;
  /**
   * `#intelLevel` is on country. Whatever `UC2_WATERSHED_ONLY_CHIPS` still names is
   * greyed here, with the reason in the title, rather than showing a country something
   * the run never computed. That list is empty since the 3 Oct round.
   */
  countryLevel?: boolean;
  /**
   * `#uc2Practice`, rendered right under the chips while the practices chip is the active
   * one — the sub-selector belongs to the chip it repaints (client §2's own row), and the
   * card is not on screen at all until something is clicked.
   */
  practiceSelect?: ReactNode;
}

export function Uc2Chips({
  classes,
  note,
  greeningOpen,
  countryLevel = false,
  practiceSelect,
}: Uc2ChipsProps) {
  const { t } = useTranslation();
  const stored = useAppStore((s) => s.intel.uc2Chip);
  const setIntelUc2Chip = useAppStore((s) => s.setIntelUc2Chip);
  const active = normaliseChip(stored);

  return (
    <div className="uc1-chipsblock">
      <h5 className="intel-block-title">{t('intel.uc2.chipsTitle')}</h5>
      <div id="uc2Chips" className="uc1-chips" role="group" aria-label={t('intel.uc2.chipsTitle')}>
        {UC2_CHIPS.map((id, i) => {
          const on = id === active;
          const watershedOnly = countryLevel && UC2_WATERSHED_ONLY_CHIPS.includes(id);
          const pending = id === 'greening' && !greeningOpen;
          const disabled = pending || watershedOnly;
          const title = watershedOnly
            ? t('intel.uc2.watershedOnly')
            : pending
              ? t('intel.uc2.greeningPending')
              : undefined;
          return (
            <ChipHelp
              key={id}
              helpId={`uc2ChipHelp-${id}`}
              title={t(`intel.uc2.help.${id}.title`)}
              what={t(`intel.uc2.help.${id}.what`)}
              classes={helpClasses(id, t)}
              align={i >= UC2_CHIPS.length / 2 ? 'right' : 'left'}
            >
              <button
                type="button"
                id={`uc2Chip-${id}`}
                className={on ? 'uc1-chip on' : 'uc1-chip'}
                aria-pressed={on}
                disabled={disabled}
                title={title}
                onClick={() => setIntelUc2Chip(id)}
              >
                {t(`intel.uc2.chip.${id}`)}
              </button>
            </ChipHelp>
          );
        })}
      </div>
      {countryLevel && UC2_WATERSHED_ONLY_CHIPS.length ? (
        // Visible, not only a `title`: a greyed chip whose reason lives in a tooltip is a
        // reason nobody on a touch screen or a keyboard ever reads. Nothing is greyed at
        // country level since the client round of 3 Oct 2026 — land-cover change and
        // greening draw their raster over the country boundaries and the units chart says
        // the country part — so the note is in the DOM only if the constant names a chip.
        <p className="note" data-testid="uc2-watershed-only">
          {t('intel.uc2.watershedOnlyNote')}
        </p>
      ) : null}
      <ul id="uc2Legend" className="uc1-legend">
        {classes.map((c) => (
          // Keyed and addressed by the class itself: a test that named the colour would
          // have to be edited every time a palette is retuned, which is the one thing
          // about a legend row that carries no meaning of its own.
          <li key={c.label} data-testid={`uc2-legend-${slug(c.label)}`}>
            <span
              className={c.hatched ? 'uc1-swatch uc1-swatch-hatched' : 'uc1-swatch'}
              style={c.hatched ? undefined : { background: c.color }}
              aria-hidden="true"
            />
            {c.n == null ? c.label : `${c.label} (${c.n})`}
          </li>
        ))}
        {note ? <li data-testid="uc2-legend-note">{note}</li> : null}
      </ul>
      {practiceSelect}
    </div>
  );
}

export default Uc2Chips;
